// ============================================================================
// Related-content engine.
//
// Every leaf content page (service, blog post, glossary term, calculator,
// template, industry) renders a mixed-type "Related" rail. We score
// candidates by keyword-token overlap with the current page's primary
// keyword + topic tags, exclude self, and return the top N across types.
// ============================================================================

import { services } from "@/content/services";
import { blogPosts } from "@/content/blog";
import { glossaryTerms } from "@/content/glossary";
import { templates } from "@/content/templates";
import { tools } from "@/content/tools";
import { industries } from "@/content/industries";

export type ContentType =
  | "service"
  | "blog"
  | "glossary"
  | "template"
  | "calculator"
  | "industry";

export interface ContentRef {
  type: ContentType;
  slug: string;
  title: string;
  url: string;
  summary: string;
  /** Primary keyword (lower-case). */
  primaryKeyword: string;
  /** Extra topic tags for matching — categories, related-term slugs, etc. */
  topics: string[];
}

const STOP = new Set([
  "a", "an", "the", "for", "in", "of", "to", "and", "or", "at", "by", "with",
  "your", "uk", "your", "guide", "is", "how", "what",
]);

function tokens(...inputs: string[]): string[] {
  return inputs
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 2 && !STOP.has(t));
}

// ────────────────────────────────────────────────────────────────────────────
// Build the unified index once at module load.
// ────────────────────────────────────────────────────────────────────────────

export const CONTENT_INDEX: ContentRef[] = [
  ...services.map<ContentRef>((s) => ({
    type: "service",
    slug: s.slug,
    title: s.name,
    url: `/services/${s.slug}`,
    summary: s.summary,
    primaryKeyword: (s.primaryKeyword ?? s.slug.replace(/-/g, " ")).toLowerCase(),
    topics: [s.shortName.toLowerCase()],
  })),
  ...blogPosts.map<ContentRef>((p) => ({
    type: "blog",
    slug: p.slug,
    title: p.title,
    url: `/hr-insights/${p.slug}`,
    summary: p.excerpt,
    primaryKeyword: (p.primaryKeyword ?? p.slug.replace(/-/g, " ")).toLowerCase(),
    topics: [p.category.toLowerCase()],
  })),
  ...glossaryTerms.map<ContentRef>((t) => ({
    type: "glossary",
    slug: t.slug,
    title: t.term,
    url: `/hr-glossary/${t.slug}`,
    summary: t.shortDefinition,
    primaryKeyword: (t.primaryKeyword ?? t.term).toLowerCase(),
    topics: t.relatedTerms ?? [],
  })),
  ...templates.map<ContentRef>((tpl) => ({
    type: "template",
    slug: tpl.slug,
    title: tpl.title,
    url: `/hr-templates/${tpl.slug}`,
    summary: tpl.description,
    primaryKeyword: (tpl.primaryKeyword ?? tpl.slug.replace(/-/g, " ")).toLowerCase(),
    topics: [tpl.category.toLowerCase()],
  })),
  ...tools.map<ContentRef>((tool) => ({
    type: "calculator",
    slug: tool.slug,
    title: tool.name,
    url: `/hr-calculators/${tool.slug}`,
    summary: tool.shortDescription,
    primaryKeyword: tool.primaryKeyword.toLowerCase(),
    topics: [tool.category.toLowerCase()],
  })),
  ...industries.map<ContentRef>((ind) => ({
    type: "industry",
    slug: ind.slug,
    title: ind.name,
    url: `/hr-by-industry/${ind.slug}`,
    summary: ind.summary,
    primaryKeyword: ind.primaryKeyword.toLowerCase(),
    topics: [ind.shortName.toLowerCase()],
  })),
];

// ────────────────────────────────────────────────────────────────────────────
// Scoring + selection
// ────────────────────────────────────────────────────────────────────────────

export interface RelatedQuery {
  type: ContentType;
  slug: string;
  primaryKeyword: string;
  topics?: string[];
  /** Max items to return (default 4). */
  limit?: number;
  /** Limit results to one item per content type (recommended for mixed rail). */
  diversify?: boolean;
}

interface Scored {
  ref: ContentRef;
  score: number;
}

export function getRelatedContent(query: RelatedQuery): ContentRef[] {
  const limit = query.limit ?? 4;
  const queryTokens = new Set(tokens(query.primaryKeyword, ...(query.topics ?? [])));

  const scored: Scored[] = CONTENT_INDEX
    .filter((c) => !(c.type === query.type && c.slug === query.slug))
    .map((c) => {
      const cTokens = new Set(tokens(c.primaryKeyword, ...c.topics));
      let overlap = 0;
      for (const t of queryTokens) if (cTokens.has(t)) overlap++;
      // Boost cross-type pairs that the user almost always wants:
      // glossary ↔ calculator, blog ↔ template, service ↔ industry.
      const bonus = crossTypeBoost(query.type, c.type);
      return { ref: c, score: overlap + bonus };
    })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score);

  if (!query.diversify) return scored.slice(0, limit).map((s) => s.ref);

  const seenTypes = new Set<ContentType>();
  const out: ContentRef[] = [];
  // First pass: top-scored item per type.
  for (const s of scored) {
    if (out.length >= limit) break;
    if (seenTypes.has(s.ref.type)) continue;
    out.push(s.ref);
    seenTypes.add(s.ref.type);
  }
  // Second pass: fill remaining slots from the rest.
  for (const s of scored) {
    if (out.length >= limit) break;
    if (out.includes(s.ref)) continue;
    out.push(s.ref);
  }
  return out;
}

function crossTypeBoost(a: ContentType, b: ContentType): number {
  const pair = [a, b].sort().join("|");
  switch (pair) {
    case "blog|template":
    case "calculator|glossary":
    case "industry|service":
    case "blog|service":
    case "blog|calculator":
      return 0.5;
    default:
      return 0;
  }
}
